import { Box, Card, Chip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import * as React from 'react';
import { DEFAULT_SHADOW } from 'styles/theme';
import { NoColorLink } from 'ui/NoColorLink';

import { GenericCardDetails } from './components/GenericCardDetails';
import { GenericCardFolderIcon } from './components/GenericCardFolderIcon';
import { GenericCardHeader } from './components/GenericCardHeader';

export type GenericCardProps = {
    title: string;
    route?: string;
    action: React.ReactNode;
    icon: React.ReactNode;
    onClick?: () => void;
    className?: string;
    subtitle?: string;
    chipIcon?: React.ReactElement;
    chipLabel?: string;
    isFolder?: boolean;
    folder?: { name: string };
    size?: 'sm' | 'md';
};

const StyGenericCardContainer = styled(Card, {
    name: 'StyGenericCardContainer',
    shouldForwardProp: (prop) => prop !== 'size' && prop !== 'hasOnClick',
})<{ size: 'sm' | 'md'; hasOnClick: boolean }>(
    ({ theme, size, hasOnClick }) => ({
        ...DEFAULT_SHADOW,
        width: size === 'sm' ? '250px' : '375px',
        height: size === 'sm' ? '150px' : '100px',
        padding: theme.spacing(2),
        gap: theme.spacing(1),
        borderRadius: theme.spacing(2),
        cursor: hasOnClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: theme.palette.darkGrey,
        backgroundColor: theme.palette.common.white,
    }),
);

const GenericCard = ({
    title,
    route = undefined,
    onClick = undefined,
    icon,
    action,
    chipIcon = undefined,
    chipLabel = undefined,
    className = undefined,
    subtitle = undefined,
    folder = undefined,
    isFolder = false,
    size = 'sm',
}: GenericCardProps): React.ReactElement => {
    const theme = useTheme();
    const content = (
        <StyGenericCardContainer
            onClick={onClick}
            className={className}
            size={size}
            hasOnClick={!!onClick || !!route}
            role={onClick ? 'button' : undefined}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: theme.spacing(1),
                }}
            >
                <GenericCardHeader
                    title={title}
                    icon={icon}
                    iconBackgroundColor={
                        isFolder ? theme.palette.success.main : undefined
                    }
                    iconSize={isFolder ? 'md' : 'sm'}
                />
                <GenericCardDetails
                    subtitle={subtitle}
                    chipLabel={chipLabel}
                    chipIcon={chipIcon}
                    showChip={!folder && !isFolder}
                    size={size}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(1),
                }}
            >
                <Box
                    sx={{
                        ml: theme.spacing(0.5),
                        display: 'flex',
                        gap: theme.spacing(1),
                        alignItems: 'center',
                    }}
                >
                    {chipLabel && (folder || isFolder) && (
                        <Chip
                            sx={{ backgroundColor: theme.palette.lightGrey }}
                            icon={chipIcon}
                            label={chipLabel}
                            size="small"
                        />
                    )}
                    <Box onClick={(e) => e.stopPropagation()}>{action}</Box>
                </Box>

                {folder && <GenericCardFolderIcon name={folder.name} />}
            </Box>
        </StyGenericCardContainer>
    );
    return route ? <NoColorLink to={route}>{content}</NoColorLink> : content;
};

export default GenericCard;
